import React, { ChangeEvent } from 'react';
import { Col, Row } from '../../../lib/utils';
import dayjs from 'dayjs';

type Props = {
    isMonth: boolean;
    isYear: boolean;
    isEntire: boolean;
    currentDate: any;
    searchText: string;
    onClickSubtractDate: () => void;
    onClickAddDate: () => void;
    settingDate: (type: string) => void;
    onClickResetButton: () => void;
    onChangeSearchText: (e: ChangeEvent<HTMLInputElement>) => void;
    thisMonthFilterList: any;
    thisYearFilterList: any;
    entireFilterList: any;
};

const ClassHistoryHeader = ({
    isMonth,
    isYear,
    isEntire,
    currentDate,
    searchText,
    onClickSubtractDate,
    onClickAddDate,
    settingDate,
    onClickResetButton,
    onChangeSearchText,
    thisMonthFilterList,
    thisYearFilterList,
    entireFilterList,
}: Props) => {
    const totalCount = isMonth
        ? thisMonthFilterList?.length ?? 0
        : isYear
          ? thisYearFilterList?.length ?? 0
          : entireFilterList?.length ?? 0;

    const dateLabel = isMonth
        ? dayjs(currentDate).format('YYYY년 M월')
        : isYear
          ? dayjs(currentDate).format('YYYY년')
          : '전체 기간';

    const segments: Array<{ key: 'month' | 'year' | 'entire'; label: string; active: boolean }> = [
        { key: 'month', label: '이번 달', active: isMonth },
        { key: 'year', label: '이번 연도', active: isYear },
        { key: 'entire', label: '전체', active: isEntire },
    ];

    return (
        <Col style={{ width: 1192, marginBottom: 24 }}>
            {/* 타이틀 + 총 건수 */}
            <Row style={{ alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 18 }}>
                <Row style={{ alignItems: 'baseline', gap: 12 }}>
                    <span style={{ fontSize: 22, fontWeight: 800, color: '#111827', letterSpacing: -0.5 }}>
                        수업 기록
                    </span>
                    <span
                        style={{
                            padding: '4px 12px',
                            borderRadius: 999,
                            backgroundColor: '#EFF6FF',
                            border: '1px solid #BFDBFE',
                            fontSize: 13,
                            fontWeight: 700,
                            color: '#1D4ED8',
                        }}
                    >
                        총 {totalCount.toLocaleString()}회
                    </span>
                </Row>
            </Row>

            {/* 필터 바 — 카드형 */}
            <Row
                style={{
                    alignItems: 'center',
                    gap: 12,
                    padding: '12px 16px',
                    backgroundColor: '#FFFFFF',
                    border: '1px solid #E2E8F0',
                    borderRadius: 14,
                    boxShadow: '0 2px 8px rgba(15,23,42,0.04)',
                    flexWrap: 'wrap',
                }}
            >
                {/* 세그먼트 컨트롤 */}
                <Row
                    style={{
                        display: 'inline-flex',
                        padding: 4,
                        borderRadius: 10,
                        backgroundColor: '#F1F5F9',
                        gap: 2,
                    }}
                >
                    {segments.map((seg) => (
                        <button
                            key={seg.key}
                            onClick={() => settingDate(seg.key)}
                            style={{
                                padding: '8px 18px',
                                borderRadius: 8,
                                fontSize: 13,
                                fontWeight: seg.active ? 700 : 500,
                                backgroundColor: seg.active ? '#FFFFFF' : 'transparent',
                                color: seg.active ? '#1D4ED8' : '#64748B',
                                border: 'none',
                                cursor: 'pointer',
                                boxShadow: seg.active ? '0 2px 6px rgba(15,23,42,0.08)' : 'none',
                                transition: 'all 0.15s',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {seg.label}
                        </button>
                    ))}
                </Row>

                {/* 날짜 네비게이터 */}
                <Row
                    style={{
                        alignItems: 'center',
                        gap: 4,
                        padding: '4px 8px',
                        backgroundColor: '#F8FAFC',
                        borderRadius: 10,
                        border: '1px solid #E2E8F0',
                        opacity: isEntire ? 0.6 : 1,
                    }}
                >
                    <button
                        onClick={onClickSubtractDate}
                        disabled={isEntire}
                        style={{
                            width: 28,
                            height: 28,
                            borderRadius: 7,
                            border: 'none',
                            background: 'transparent',
                            color: isEntire ? '#CBD5E1' : '#475569',
                            fontSize: 16,
                            fontWeight: 700,
                            cursor: isEntire ? 'default' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        ‹
                    </button>
                    <span
                        style={{
                            minWidth: 120,
                            textAlign: 'center',
                            fontSize: 14,
                            fontWeight: 700,
                            color: '#1F2937',
                            padding: '0 8px',
                        }}
                    >
                        {dateLabel}
                    </span>
                    <button
                        onClick={onClickAddDate}
                        disabled={isEntire}
                        style={{
                            width: 28,
                            height: 28,
                            borderRadius: 7,
                            border: 'none',
                            background: 'transparent',
                            color: isEntire ? '#CBD5E1' : '#475569',
                            fontSize: 16,
                            fontWeight: 700,
                            cursor: isEntire ? 'default' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        ›
                    </button>
                </Row>

                {/* 초기화 버튼 */}
                <button
                    onClick={onClickResetButton}
                    style={{
                        padding: '8px 12px',
                        borderRadius: 9,
                        border: '1px solid #E2E8F0',
                        backgroundColor: '#FFFFFF',
                        color: '#64748B',
                        fontSize: 12,
                        fontWeight: 600,
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 4,
                        whiteSpace: 'nowrap',
                    }}
                >
                    <span style={{ fontSize: 12 }}>↺</span>
                    초기화
                </button>

                {/* 검색 — 우측 정렬 */}
                <div style={{ flex: 1 }} />
                <Row
                    style={{
                        alignItems: 'center',
                        gap: 8,
                        padding: '0 14px',
                        height: 40,
                        width: 300,
                        borderRadius: 10,
                        backgroundColor: '#F8FAFC',
                        border: '1px solid #E2E8F0',
                    }}
                >
                    <span style={{ fontSize: 13, color: '#94A3B8' }}>🔍</span>
                    <input
                        value={searchText}
                        onChange={onChangeSearchText}
                        placeholder="회원 이름 검색"
                        style={{
                            flex: 1,
                            border: 'none',
                            outline: 'none',
                            backgroundColor: 'transparent',
                            fontSize: 14,
                            color: '#1F2937',
                        }}
                    />
                    {searchText && (
                        <button
                            onClick={() => {
                                const fakeEvent = { target: { value: '' } } as ChangeEvent<HTMLInputElement>;
                                onChangeSearchText(fakeEvent);
                            }}
                            style={{
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                color: '#94A3B8',
                                fontSize: 13,
                            }}
                        >
                            ✕
                        </button>
                    )}
                </Row>
            </Row>

            {/* 서브 정보 (검색 힌트) */}
            {searchText && (
                <Row style={{ marginTop: 10, paddingLeft: 4 }}>
                    <span style={{ fontSize: 12, color: '#64748B' }}>
                        <b style={{ color: '#1D4ED8' }}>'{searchText}'</b> 검색 결과 · {totalCount.toLocaleString()}건
                    </span>
                </Row>
            )}
        </Col>
    );
};

export default ClassHistoryHeader;
